import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { MIN_ADULTS } from '../constants/tripSearchParty';

/**
 * TripSearch — the destination/dates/guests the user last selected in the
 * HeroSearch bar or the phone trip search sheet. Persisted to localStorage so it survives navigation and
 * page reloads; consumed by accommodation listings (to filter by capacity)
 * and detail-page booking cards (to prefill the guest count).
 *
 * `guests` is the total party size and always equals `adults + children`;
 * the writers below derive it, so a patch's own `guests` value is ignored.
 * Only HeroSearch, useTripSearchEditor and the added-to-cart "Browse services" and
 * "Find a stay" links call the writers; detail pages read.
 * The `adults` / `children` split is preserved so accommodation booking cards
 * can prefill the granular occupancy rather than collapsing everyone into
 * adults; capacity filtering that only needs a head-count reads `guests`.
 * Detail-page components clamp these against per-listing min/max bounds.
 */
export interface TripSearch {
  destination: string;
  /** ISO yyyy-mm-dd or null when not picked */
  checkin: string | null;
  /** ISO yyyy-mm-dd or null when not picked */
  checkout: string | null;
  /** Total party size — invariant: equals `adults + children`. */
  guests: number;
  adults: number;
  children: number;
}

interface StoredTripSearch {
  trip: TripSearch;
  searchedGuests: number;
}

export const EMPTY_TRIP_SEARCH: TripSearch = {
  destination: '',
  checkin: null,
  checkout: null,
  guests: MIN_ADULTS,
  adults: MIN_ADULTS,
  children: 0,
};

/** The starting party of one adult alone, which the bar, URL and filter leave out. */
export function isDefaultParty(trip: TripSearch): boolean {
  return trip.adults === MIN_ADULTS && trip.children === 0;
}

interface TripSearchContextValue {
  tripSearch: TripSearch;
  /** Party total of the last submitted search; the only value results filtering reads. */
  searchedGuests: number;
  updateTripSearch: (patch: Partial<TripSearch>) => void;
  submitTripSearch: (patch: Partial<TripSearch>) => void;
  clearTripSearch: () => void;
}

const TripSearchContext = createContext<TripSearchContextValue | undefined>(undefined);

const STORAGE_KEY = 'belhop_trip_search';

const EMPTY_STORED: StoredTripSearch = { trip: EMPTY_TRIP_SEARCH, searchedGuests: 0 };

function readStored(): StoredTripSearch {
  if (typeof window === 'undefined') return EMPTY_STORED;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return EMPTY_STORED;
    const parsed = JSON.parse(raw) as Partial<TripSearch> & { searchedGuests?: unknown };
    const storedAdults = typeof parsed.adults === 'number' && parsed.adults > 0 ? parsed.adults : 0;
    const storedChildren =
      typeof parsed.children === 'number' && parsed.children > 0 ? parsed.children : 0;
    const storedGuests = typeof parsed.guests === 'number' && parsed.guests > 0 ? parsed.guests : 0;
    // Back-compat: entries persisted before the split carried only `guests`.
    // Attribute the whole party to adults so the bar still shows a count and
    // the `guests === adults + children` invariant holds. New writes always
    // carry the real split.
    const hasSplit = storedAdults > 0 || storedChildren > 0;
    const adults = Math.max(MIN_ADULTS, hasSplit ? storedAdults : storedGuests);
    const trip: TripSearch = {
      destination: typeof parsed.destination === 'string' ? parsed.destination : '',
      checkin: typeof parsed.checkin === 'string' ? parsed.checkin : null,
      checkout: typeof parsed.checkout === 'string' ? parsed.checkout : null,
      guests: adults + storedChildren,
      adults,
      children: storedChildren,
    };
    // Back-compat: an entry written before the submitted party was stored
    // carried only the party that Search had saved, so it is the submitted one.
    const searchedGuests =
      typeof parsed.searchedGuests === 'number' && parsed.searchedGuests >= 0
        ? parsed.searchedGuests
        : submittedPartyOf(trip);
    return { trip, searchedGuests };
  } catch {
    return EMPTY_STORED;
  }
}

/** Merges a patch, holds the adult minimum and re-derives `guests`, so no caller can break either. */
function mergeTrip(trip: TripSearch, patch: Partial<TripSearch>): TripSearch {
  const merged = { ...trip, ...patch };
  const adults = Math.max(MIN_ADULTS, merged.adults);
  return { ...merged, adults, guests: adults + merged.children };
}

/** The party results filter by: none for the default party, so it filters nothing. */
function submittedPartyOf(trip: TripSearch): number {
  return isDefaultParty(trip) ? 0 : trip.guests;
}

export const TripSearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [stored, setStored] = useState<StoredTripSearch>(readStored);
  const { trip: tripSearch, searchedGuests } = stored;

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...tripSearch, searchedGuests }));
    } catch {
      // localStorage may be unavailable (private mode / quota). Failing here
      // would surface as a thrown render error — silently drop instead, the
      // in-memory state still works for the session.
    }
  }, [tripSearch, searchedGuests]);

  const updateTripSearch = useCallback((patch: Partial<TripSearch>) => {
    setStored((prev) => ({ ...prev, trip: mergeTrip(prev.trip, patch) }));
  }, []);

  /** The Search action: saves the picks and records their party as the submitted one. */
  const submitTripSearch = useCallback((patch: Partial<TripSearch>) => {
    setStored((prev) => {
      const trip = mergeTrip(prev.trip, patch);
      return { trip, searchedGuests: submittedPartyOf(trip) };
    });
  }, []);

  const clearTripSearch = useCallback(() => setStored(EMPTY_STORED), []);

  return (
    <TripSearchContext.Provider
      value={{ tripSearch, searchedGuests, updateTripSearch, submitTripSearch, clearTripSearch }}
    >
      {children}
    </TripSearchContext.Provider>
  );
};

export const useTripSearch = (): TripSearchContextValue => {
  const ctx = useContext(TripSearchContext);
  if (!ctx) {
    throw new Error('useTripSearch must be used within a TripSearchProvider');
  }
  return ctx;
};
